import { Link } from "react-router-dom";
import { Lightning, ArrowLeft } from "@phosphor-icons/react";

export default function Terms() {
    return (
        <div className="min-h-screen bg-background relative">
            <header className="border-b border-white/10 px-6 md:px-12 py-4 flex justify-between items-center">
                <Link to="/" data-testid="terms-logo" className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-electric-blue flex items-center justify-center neon-border">
                        <Lightning weight="fill" size={18} className="text-white" />
                    </div>
                    <span className="font-heading font-black tracking-tighter text-lg">
                        NEXUS<span className="text-electric-blue">.</span>LAB
                    </span>
                </Link>
                <Link
                    to="/"
                    data-testid="terms-back-link"
                    className="label-mono text-xs hover:text-electric-blue flex items-center gap-1.5 transition-colors"
                >
                    <ArrowLeft size={12} weight="bold" /> BACK
                </Link>
            </header>

            <article className="max-w-3xl mx-auto px-6 md:px-12 py-16 prose prose-invert">
                <p className="label-mono text-electric-blue mb-3">LEGAL // TERMS OF SERVICE</p>
                <h1 className="font-heading text-5xl font-black tracking-tighter mb-2">Terms of Service</h1>
                <p className="text-muted-foreground mb-12">Effective Date: April 26, 2026</p>

                <Section title="1. The Lab You're Joining">
                    Nexus Creator Lab ("Nexus", "we", "us", or "the Lab") is a digital membership platform
                    operated by Nexus Creative Group LLC. By creating an account or paying for access, you
                    ("Operator", "Member", or "you") agree to these Terms in full.
                </Section>

                <Section title="2. Membership & Founder Cohort">
                    <p>The Founder Cohort is limited to 1,000 lifetime seats. Once Seat #1,000 is claimed, new memberships are paused until 2027 (or such date as we publicly announce).</p>
                    <p>Two tiers are available:</p>
                    <ul>
                        <li><strong>Founder ($20 USD/month):</strong> Recurring billing. Cancel anytime. Locked-in rate for the life of your active membership.</li>
                        <li><strong>Lifetime Vault Access ($497 USD one-time):</strong> Single payment, no recurring charge. Permanent access to all Recipes, Playbooks, and future content.</li>
                    </ul>
                </Section>

                <Section title="3. Payment, Refunds, and Cancellation">
                    <p>All payments are processed by Stripe. Recurring charges renew on the same calendar day each month until canceled.</p>
                    <p><strong>Refund Policy:</strong> Founder-tier members may cancel at any time from their dashboard; access remains active through the end of the paid billing period. Lifetime payments are non-refundable after 14 days. Refund requests within the 14-day window must be sent to <a href="mailto:contact@nexuscreatorlab.com" className="text-electric-blue">contact@nexuscreatorlab.com</a>.</p>
                </Section>

                <Section title="4. Content License & Acceptable Use">
                    <p>All Recipes, Playbooks, prompts, scripts, and Nexus-generated PDFs are licensed to you for personal use as a faceless creator. You may not:</p>
                    <ul>
                        <li>Resell, redistribute, or repackage Lab content as your own product;</li>
                        <li>Share your account credentials with non-members;</li>
                        <li>Train AI models on Lab content without written permission;</li>
                        <li>Use Lab tools to produce content that violates any platform's terms (TikTok, YouTube, Meta, etc.) or any applicable law.</li>
                    </ul>
                    <p>Violations may result in immediate, non-refundable termination of your membership.</p>
                </Section>

                <Section title="5. AI-Generated Content">
                    <p>The Nexus Script Engine uses Google's Gemini 3 Flash model to generate video scripts. You retain ownership of scripts you generate, but you are solely responsible for verifying accuracy, originality, and compliance with applicable laws before publishing.</p>
                    <p>We do not guarantee specific performance, view counts, RPM, or growth metrics. The Lab provides tools and frameworks — outcomes depend on your execution.</p>
                </Section>

                <Section title="6. User-Generated Content (Feedback Lounge)">
                    Posts you make in the Feedback Lounge are visible to other Members. By posting, you grant Nexus a non-exclusive, perpetual license to display, moderate, and remove your posts at our discretion. Do not post personally identifying information about non-consenting third parties.
                </Section>

                <Section title="7. Disclaimers & Limitation of Liability">
                    <p>The Lab is provided "as is" without warranty of any kind. Nexus Creative Group LLC is not liable for any indirect, incidental, special, or consequential damages arising from your use of the platform.</p>
                    <p>Our maximum aggregate liability is limited to the total amount you paid us in the 12 months preceding any claim.</p>
                </Section>

                <Section title="8. Termination">
                    We may suspend or terminate accounts that violate these Terms. You may delete your account at any time by contacting support; lifetime members forfeit access upon voluntary deletion.
                </Section>

                <Section title="9. Changes to These Terms">
                    We may update these Terms periodically. Material changes will be communicated by email and posted with an updated effective date. Continued use of the Lab after the effective date constitutes acceptance.
                </Section>

                <Section title="10. Governing Law & Contact">
                    These Terms are governed by the laws of the United States. For any questions, reach us at{" "}
                    <a href="mailto:contact@nexuscreatorlab.com" className="text-electric-blue">contact@nexuscreatorlab.com</a>.
                </Section>

                <p className="text-xs text-muted-foreground italic mt-16 border-t border-white/10 pt-6">
                    These Terms are provided as a starting template and do not constitute legal advice. Consult a licensed attorney before final publication.
                </p>
            </article>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-10">
            <h2 className="font-heading text-xl font-bold text-electric-blue mb-3 tracking-tight">{title}</h2>
            <div className="text-sm text-foreground/85 leading-relaxed space-y-3">{children}</div>
        </section>
    );
}
